import { cityContext } from '../../services/cityContext';

export interface QueryValidationResult {
  type: 'weather' | 'travel' | 'general' | 'itinerary' | 'location_info' | 'restaurant' | 'help' | 'flight' | 'visa' | 'budget' | 'advisory' | 'festival' | 'currency' | 'language';
  location?: string;
  shouldBlock: boolean;
  isExplicitLocationRequest: boolean;
  confidence?: number; // Added from WizardFactory
  // New fields for itinerary planning
  itineraryInfo?: {
    destination?: string;
    startDate?: string;
    endDate?: string;
    travelGroup?: string;
    interests?: string[];
    missingFields: Array<'destination' | 'dates' | 'travelGroup' | 'interests'>;
    isComplete: boolean;
  };
}


// LLM-based intent detection
async function detectIntentWithLLM(message: string): Promise<{
  type: string;
  confidence: number;
  possibleFields: string[];
  rawResponse?: string; // Add this field
}> {
  try {
    const response = await fetch('/api/wizard/detect-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({ 
        message,
        categories: [
          "weather", // Weather info
          "travel", // General travel
          "itinerary", // Trip planning
          "location_info", // Info about places
          "restaurant", // Dining info
          "help", // Add this
          "general", // Other queries
          "flight", // Flight info
          "visa", // Visa info
          "budget", // Budget planning
          "advisory", // Travel advisories
          "festival", // Festivals and events
          "currency", // Currency exchange
          "language" // Language phrases
        ],
        includeRawResponse: true // Request raw response from API
      })
    });
    
    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error("Intent detection error:", error);
    return { 
      type: 'general', 
      confidence: 0.1,
      possibleFields: []
    };
  }
}


/* export async function validateQueryAsync_UNUSED(content: string): Promise<QueryValidationResult> {
  console.log('[QueryValidator] Processing query:', content);

  try {
    // Step 1: Try LLM intent detection
    const llmResult = await detectIntentWithLLM(content);
    console.log('[QueryValidator] LLM intent detection result:', llmResult);
    
    // Step 2: Use LLM result if confident
    if (llmResult && llmResult.confidence > 0.7) {
      //const location = extractLocation(content);
      
      const result: QueryValidationResult = {
        type: llmResult.type as any,
        location,
        shouldBlock: llmResult.type === 'weather',
        isExplicitLocationRequest: llmResult.type === 'travel',
        confidence: llmResult.confidence
      };
      
      console.log('[QueryValidator] Final result (LLM-based):', result);
      return result;
    }
  } catch (error) {
    console.error('[QueryValidator] Error in LLM intent detection:', error);
    // Continue to fallback on error
  }
  
  // Step 3: Fall back for low-confidence results or on error
  console.log('[QueryValidator] Using default general intent type');
  return {
    type: 'general',
    shouldBlock: false,
    isExplicitLocationRequest: false,
    confidence: 0.1
  };
} */

// In queryValidator.ts - modify the validateQueryAsync function
export async function validateQueryAsync(content: string): Promise<QueryValidationResult> {
  console.log('[QueryValidator] Processing query:', content);

  // Check for help first (before LLM)
  const normalizedContent = content.toLowerCase().trim();
  if (/^(?:help|help me|need help|i need help|can you help|help please)$/i.test(normalizedContent)) {
    return {
      type: 'help',
      shouldBlock: true,
      isExplicitLocationRequest: false,
      confidence: 0.95
    };
  }

  try {
    const llmResult = await detectIntentWithLLM(content);
    console.log('[QueryValidator] LLM intent detection result:', llmResult);
    
    if (llmResult && llmResult.confidence > 0.7) {
      // Extract location from embedded JSON or fall back to regex
      let location = llmResult.city;

      //(llmResult.rawResponse && extractLocationFromEmbeddedJSON(llmResult.rawResponse)) || extractLocation(content);
      
      // Update city context if location is found
      if (location) {
        console.log(`[QueryValidator] Setting city context to: ${location}`);
        cityContext.setCurrentCity(location);
      }
      
      const result: QueryValidationResult = {
        type: llmResult.type as any,
        location,
        shouldBlock: llmResult.type === 'weather',
        isExplicitLocationRequest: llmResult.type === 'travel',
        confidence: llmResult.confidence
      };
      
      console.log('[QueryValidator] Final result (LLM-based):', result);
      return result;
    }
  } catch (error) {
    console.error('[QueryValidator] Error in LLM intent detection:', error);
  }
  
  // Fallback to regex extraction
  const location = extractLocation(content);
  
  // Even in fallback, update city context if location found
  if (location) {
    console.log(`[QueryValidator] Setting city context from fallback to: ${location}`);
    cityContext.setCurrentCity(location);
  }
  
  // Fallback
  return {
    type: 'general',
    location, // Include the location if found
    shouldBlock: false,
    isExplicitLocationRequest: false,
    confidence: 0.1
  };
}

// Update the location extraction in validateQueryAsync
export async function validateQueryAsync_ORIGINAL(content: string): Promise<QueryValidationResult> {
  console.log('[QueryValidator] Processing query:', content);

    // Check for help first (before LLM)
  const normalizedContent = content.toLowerCase().trim();
  if (/^(?:help|help me|need help|i need help|can you help|help please)$/i.test(normalizedContent)) {
    return {
      type: 'help',
      shouldBlock: true,
      isExplicitLocationRequest: false,
      confidence: 0.95
    };
  }

  try {
    const llmResult = await detectIntentWithLLM(content);
    console.log('[QueryValidator] LLM intent detection result:', llmResult);
    
    if (llmResult && llmResult.confidence > 0.7) {
      // Extract location from embedded JSON or fall back to regex
      const location = (llmResult.rawResponse && extractLocationFromEmbeddedJSON(llmResult.rawResponse)) || 
                       extractLocation(content);
      
      const result: QueryValidationResult = {
        type: llmResult.type as any,
        location,
        shouldBlock: llmResult.type === 'weather',
        isExplicitLocationRequest: llmResult.type === 'travel',
        confidence: llmResult.confidence
      };
      
      console.log('[QueryValidator] Final result (LLM-based):', result);
      return result;
    }
  } catch (error) {
    console.error('[QueryValidator] Error in LLM intent detection:', error);
  }
  
  // Fallback
  return {
    type: 'general',
    shouldBlock: false,
    isExplicitLocationRequest: false,
    confidence: 0.1
  };
}


export async function validateQueryHybrid(content: string): Promise<QueryValidationResult> {
  console.log('[QueryValidator] Processing with hybrid approach:', content);
  
  // Check for help first
  const normalizedContent = content.toLowerCase().trim();
  if (/^(?:help|help me|need help|i need help|can you help|help please)$/i.test(normalizedContent)) {
    return {
      type: 'help',
      shouldBlock: true,
      isExplicitLocationRequest: false,
      confidence: 0.95
    };
  }
  // Step 1: Try regex patterns first (fast)
  const regexResult = validateQueryWithPatterns(content);
  
  // Step 2: Return immediately if regex is confident
  if (regexResult.confidence && regexResult.confidence > 0.7) {
    return regexResult;
  }
  
  // Step 3: Fall back to LLM if regex is inconclusive
  try {
    const llmResult = await detectIntentWithLLM(content);
    
    if (llmResult && llmResult.confidence > 0.6) {
      const location = regexResult.location || extractLocation(content);
      
      return {
        type: llmResult.type as any,
        location,
        shouldBlock: llmResult.type === 'weather',
        isExplicitLocationRequest: llmResult.type === 'travel',
        confidence: llmResult.confidence
      };
    }
  } catch (error) {
    console.error('[QueryValidator] LLM detection error:', error);
  }
  
  // Step 4: Use regex result as fallback
  return regexResult;
}

function validateQueryWithPatterns(content: string): QueryValidationResult {
  const normalizedContent = content.toLowerCase().trim();
  
  // Define patterns with confidence scores
  const patterns = [
    { type: 'weather', regex: /\b(?:weather|temperature|forecast|climate|rain|snow|sunny|cloudy|humidity)\b/i, confidence: 0.85 },
    { type: 'itinerary', regex: /\b(?:plan (?:a |my )?trip|create (?:a |my )?(?:travel plan|itinerary)|need (?:a |my )?itinerary|make (?:a |my )?(?:travel plan|itinerary)|help me plan)\b/i, confidence: 0.9 },
    { type: 'location_info', regex: /^\s*(?:tell me about|what is|how is|what's|describe|give me info on)\s+/i, confidence: 0.8 },
    { type: 'travel', regex: /\b(?:show me|what (?:attractions|places|spots|can i see)|tourist spots|things to do|must-visit|places to explore)\b/i, confidence: 0.75 },
    { type: 'travel', regex: /\b(?:visit|travel|explore|tour|attraction|place|destination|sight|landmark)\b/i, confidence: 0.6 },
    { type: 'help', regex: /^(?:help|help me|need help|i need help|can you help|help please)$/i, confidence: 0.95 },
  ];
  
  // Find highest confidence match
  let bestMatch = { type: 'general', confidence: 0.1 };
  
  for (const pattern of patterns) {
    if (pattern.regex.test(normalizedContent) && pattern.confidence > bestMatch.confidence) {
      bestMatch = { type: pattern.type as any, confidence: pattern.confidence };
    }
  }
  
  const location = extractLocation(normalizedContent);
  
  return {
    type: bestMatch.type as any,
    location,
    shouldBlock: bestMatch.type === 'weather',
    isExplicitLocationRequest: bestMatch.type === 'travel',
    confidence: bestMatch.confidence
  };
}


const STOP_WORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of',
  'with', 'by', 'from', 'up', 'about', 'into', 'over', 'after',
  'weather', 'temperature', 'forecast', 'climate', 'like', 'whats', "what's",
  'show', 'me', 'tell', 'check', 'current', 'now', 'today', 'please',
  'how', 'is', 'it', 'are', 'can', 'you', 'get', 'give', 'want', 'would',
  'will', 'should', 'could', 'there'
]);

function extractLocation(content: string): string | undefined {

  const patterns = [
    /trip to\s+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)/i,
    /(?:in|at|visit)\s+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)/i
  ];

  for (const pattern of patterns) {
    const match = content.match(pattern);
    if (match && match[1]) {
      return match[1].trim();
    }
  }
  
  // First try to find location after prepositions
  const prepositionMatch = content.match(/(?:in|at|for|near|around|to)\s+([^.,?!]+)/i);
  if (prepositionMatch) {
    const locationCandidate = cleanLocationText(prepositionMatch[1]);
    if (locationCandidate) {
      return locationCandidate;
    }
  }

  // Then try to find any remaining potential location
  const words = content.split(/\s+/);
  const locationWords: string[] = [];
  
  for (let i = 0; i < words.length; i++) {
    const word = words[i].toLowerCase();
    // Skip if it's a stop word
    if (STOP_WORDS.has(word)) continue;
    
    // If it starts with a capital letter in original text, it's more likely to be a location
    if (words[i][0]?.toUpperCase() === words[i][0]) {
      locationWords.push(words[i]);
      // Include the next word if it's also capitalized (e.g., "New York")
      if (words[i + 1] && words[i + 1][0]?.toUpperCase() === words[i + 1][0]) {
        locationWords.push(words[i + 1]);
        i++; // Skip the next word since we've included it
      }
    }
  }

  if (locationWords.length > 0) {
    const locationCandidate = cleanLocationText(locationWords.join(' '));
    if (locationCandidate) {
      return locationCandidate;
    }
  }

  return undefined;
}


// New function to extract location from embedded JSON in responses
function extractLocationFromEmbeddedJSON(text: string): string | undefined {
  // Match JSON-like objects
  const jsonRegex = /\{[\s\S]*?\}/g;
  const jsonMatches = text.match(jsonRegex);
  
  if (jsonMatches) {
    for (const jsonString of jsonMatches) {
      try {
        const data = JSON.parse(jsonString);
        // Check for location fields
        if (data.name || data.city || data.country) {
          const locationParts = [];
          if (data.name) locationParts.push(data.name);
          if (data.city && data.city !== data.name) locationParts.push(data.city);
          if (data.country) locationParts.push(data.country);
          return locationParts.join(', ');
        }
      } catch (e) {
        continue; // Invalid JSON
      }
    }
  }
  return undefined;
}

function cleanLocationText(text: string): string | undefined {
  // Remove punctuation and extra spaces
  const cleaned = text.trim()
    .replace(/[.,!?;:]/g, '')
    .replace(/\s+/g, ' ');

  // Split into words and filter out stop words
  const words = cleaned.split(' ')
    .filter(word => !STOP_WORDS.has(word.toLowerCase()));

  // Only return if we have at least one word and it's at least 3 chars
  if (words.length > 0 && words.join(' ').length >= 3) {
    return words.join(' ');
  }

  return undefined;
}